import { memo, useCallback, useState, useMemo, useEffect, useRef } from "react";
import { X, CaretDown, Check, WarningCircle, ArrowClockwise } from "@phosphor-icons/react";
import PropTypes from 'prop-types';
import CancelUploadModal from "./CancelUploadModal";
import {
  EnhancedNotificationWrapper,
  CollapsedNotificationWrapper,
  HeaderControls,
  HeaderLeft,
  HeaderRight,
  TabContainer,
  Tab,
  ContentArea,
  FileItem,
  FileIcon,
  FileInfo,
  FileName,
  FileSize,
  ClientLabel,
  ProgressBar,
  ProgressFill,
  ProgressText,
  SuccessIcon,
  RemoveButton,
  EmptyState,
  BottomStatus,
  StatusRow,
  SuccessStatusIcon,
  StatusText,
  CollapsedStatusContainer,
  CollapsedHeaderControls,
  CollapsedHeaderLeft,
  CollapsedHeaderRight,
  CollapsibleIcon,
  CloseIcon,
  RotatedIcon,
  LoadingSpinner,
  ErrorMessage,
  RenameRow,
  RenameInput,
  RenameRetryButton,
  RetryButton,
  RetryAllRow,
  RetryAllHint,
  ResumeBox,
  ResumeHeadline,
  ResumeText,
  ResumeButtons,
} from './EnhancedUploadNotificationStyled';
import { formatFileSize, getFileIcon } from "../../utils/fileUtils";
import { isRetryableFailedUpload } from "../../utils/uploadUtils";
import { isInterruptedUpload } from "../../utils/uploadTrayStorage";
import { useUploadNotification } from "../../context/UploadNotificationContext";

// One row of the Uploads tab. Memoised so that a progress tick on one file
// re-renders that one row, not every row: a 1,200-file claim-file drop used
// to redraw all 1,200 rows for every tick of each of the ten active
// transfers, which pinned the browser's main thread until the tab died
// (2026-10-11, production).
const UploadRow = memo(function UploadRow({ upload, onCancel }) {
  const isUploading = upload.status !== 'completed';
  return (
    <FileItem>
      <FileIcon>
        {getFileIcon(upload.fileName, upload.type)}
      </FileIcon>
      <FileInfo>
        {upload.matterLabel && (
          <ClientLabel title={upload.matterLabel} data-testid={`upload-client-${upload.id}`}>
            {upload.matterLabel}
          </ClientLabel>
        )}
        <FileName title={upload.fileName}>{upload.fileName}</FileName>
        <FileSize>{formatFileSize(upload.fileSize, upload.type)}</FileSize>
        {isUploading && (
          <>
            <ProgressBar>
              <ProgressFill progress={upload.progress} />
            </ProgressBar>
            <ProgressText>
              <span>{upload.progress}%</span>
            </ProgressText>
          </>
        )}
      </FileInfo>
      {upload.progress === 100 && upload.status === 'completed' ? (
        <SuccessIcon>
          <Check size={12} />
        </SuccessIcon>
      ) : (
        <RemoveButton onClick={() => onCancel(upload.id)} data-testid={`upload-remove-btn-${upload.id}`}>
          <X size={14} />
        </RemoveButton>
      )}
    </FileItem>
  );
});

UploadRow.propTypes = {
  upload: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const EnhancedUploadNotification = ({ 
  uploads = [], 
  failedUploads = [], 
  onClose, 
  onRemoveUpload,
  onRetryUpload,
  onRetryAllFailed,
  onRenameAndRetry,
  onResumeInterrupted,
  onCancelAll
}) => {
  const [activeTab, setActiveTab] = useState('uploads');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [uploadToCancel, setUploadToCancel] = useState(null);
  const prevTotalFailedRef = useRef(0);
  const prevUploadsInProgressRef = useRef(null);
  // Draft filenames for the "rename and retry" control on duplicate-file
  // rejections, keyed by failed-upload id. Defaults to the name that
  // collided so the user only has to change the part that matters.
  const [renameDrafts, setRenameDrafts] = useState({});
  const [renamingIds, setRenamingIds] = useState({});
  // In-flight one-click retries, keyed by failed-upload id, plus the
  // "Retry all" sweep so buttons disable instead of double-firing.
  const [retryingIds, setRetryingIds] = useState({});
  const [isRetryingAll, setIsRetryingAll] = useState(false);
  const retryableFailed = useMemo(
    () => failedUploads.filter(isRetryableFailedUpload),
    [failedUploads]
  );
  const handleRetryAll = async () => {
    if (isRetryingAll || !onRetryAllFailed) return;
    setIsRetryingAll(true);
    try {
      await onRetryAllFailed(retryableFailed);
    } finally {
      setIsRetryingAll(false);
    }
  };
  
  // Files the page reload cut off. Their bytes are gone (a browser will not
  // re-read a file from disk on its own), so the user picks them again and
  // only the ones the tray recognises are sent, each to its original client.
  const interruptedFailed = useMemo(
    () => failedUploads.filter(isInterruptedUpload),
    [failedUploads]
  );
  const [isResuming, setIsResuming] = useState(false);
  const [resumeSummary, setResumeSummary] = useState(null);
  const [confirmForget, setConfirmForget] = useState(false);
  const resumeFilesInputRef = useRef(null);
  const resumeFolderInputRef = useRef(null);

  const handleResumePicked = async (event) => {
    const picked = Array.from(event.target.files || []);
    // Let the same files be picked again later.
    event.target.value = '';
    if (picked.length === 0 || !onResumeInterrupted || isResuming) return;
    setIsResuming(true);
    setResumeSummary(null);
    try {
      setResumeSummary(await onResumeInterrupted(picked));
    } finally {
      setIsResuming(false);
    }
  };

  // Get optimization state from context (reactive)
  const { uploadedDocIds = [], optimizedDocIds = [], isUploadComplete = false } = useUploadNotification();
  
  const getDeclaredTotalUploads = (upload) => {
    const total = Number(upload?.totalUploads);
    return Number.isFinite(total) && total > 0 ? total : 0;
  };

  const totalUploads = uploads.length;
  const totalFailed = failedUploads.length;
  const completedUploads = uploads.filter(upload => upload.status === 'completed').length;
  const uploadsInProgress = uploads.filter(upload => upload.status !== 'completed').length;
  // Uploads that opt out of the matter document "optimize for fast web view"
  // pipeline (e.g. My Files personal storage — see MyFiles.js — which never
  // runs that step) via `skipOptimization: true`. Without this, a completed
  // batch made up entirely of such uploads would sit on "Optimizing files…"
  // forever, since the global uploadedDocIds/optimizedDocIds counters below
  // never get populated for them.
  const uploadsNeedingOptimization = uploads.filter(
    upload => upload.status === 'completed' && !upload.skipOptimization
  ).length;
  const declaredTotalItems = Math.max(
    0,
    ...uploads.map(getDeclaredTotalUploads),
    ...failedUploads.map(getDeclaredTotalUploads)
  );
  const totalItems = Math.max(totalUploads + totalFailed, declaredTotalItems);
  const processedUploads = Math.min(completedUploads + totalFailed, totalItems);
  
  const totalUploadedFiles = uploadedDocIds.length;
  const totalOptimizedFiles = optimizedDocIds.length;
  
  // Determine upload status
  const getUploadStatus = () => {
    // Upload In Progress: Upload is ongoing
    if (uploadsInProgress > 0) {
      return { status: 'upload_in_progress', message: `Uploading ${processedUploads} of ${totalItems} items` };
    }
    
    // When we have failed uploads and nothing in progress, show failed - not optimization loader
    if (totalFailed > 0 && uploadsInProgress === 0) {
      return { status: 'upload_failed_only', message: null };
    }

    // None of the completed uploads in this batch need optimization (e.g. a
    // My Files batch) — done is done, skip the optimization stage entirely.
    if (!uploadsInProgress && completedUploads > 0 && uploadsNeedingOptimization === 0) {
      return { status: 'upload_complete', message: 'Upload Complete' };
    }

    // If uploads are complete but we have uploaded files, show optimization status
    // This handles the transition smoothly - even if isUploadComplete is not set yet
    if (!uploadsInProgress && totalUploadedFiles > 0) {
      // Optimization Pending: Optimized files count < total uploaded files
      if (totalOptimizedFiles < totalUploadedFiles) {
        return { status: 'optimization_pending', message: `Optimizing files for fast web view…` };
      }
      
      // Upload Complete: All files optimized
      if (totalOptimizedFiles === totalUploadedFiles) {
        return { status: 'upload_complete', message: 'Upload Complete' };
      }
    }
    
    // If uploads are complete and isUploadComplete is set, use that
    if (isUploadComplete) {
      // Optimization Pending: Upload completed and optimized files count < total uploaded files
      if (totalOptimizedFiles < totalUploadedFiles && totalUploadedFiles > 0) {
        return { status: 'optimization_pending', message: `Optimizing files for fast web view…` };
      }
      
      // Upload Complete: Optimized files count equals total uploaded files
      if (totalUploadedFiles > 0 && totalOptimizedFiles === totalUploadedFiles) {
        return { status: 'upload_complete', message: 'Upload Complete' };
      }
      
      // Upload Complete: No files uploaded (edge case)
      if (totalUploadedFiles === 0) {
        return { status: 'upload_complete', message: 'Upload Complete' };
      }
    }
    
    // Default: Upload completed (for cases where optimization is not used or not tracked)
    // This handles the transition period when uploads just finished
    if (!uploadsInProgress && completedUploads > 0) {
      // If we have uploaded files, show optimization pending (even if count is 0 temporarily)
      // This prevents empty status during the brief moment when state is syncing
      if (totalUploadedFiles > 0) {
        return { status: 'optimization_pending', message: `Optimizing files for fast web view…` };
      }
      // If no uploaded files tracked yet but uploads are complete, show optimization pending
      // This is a transition state - we'll update to complete once we confirm no optimization needed
      return { status: 'optimization_pending', message: `Optimizing files for fast web view…` };
    }
    
    // Fallback: Show optimization pending if we have any completed uploads
    // This ensures we never show empty status during transitions
    if (completedUploads > 0) {
      return { status: 'optimization_pending', message: `Optimizing files for fast web view…` };
    }
    
    // Final fallback - should rarely reach here
    return { status: 'optimization_pending', message: `Optimizing files for fast web view…` };
  };
  
  const uploadStatus = getUploadStatus();
  
  // Set active tab to 'failed' if there are any failed uploads on first time they appear
  // OR when all uploads are complete and there are failed uploads
  useEffect(() => {
    // Switch to 'failed' tab when totalFailed transitions from 0 to > 0
    if (totalFailed > 0 && prevTotalFailedRef.current === 0 && activeTab === 'uploads') {
      setActiveTab('failed');
    }
    // Switch to 'failed' tab when all uploads complete (transition from in-progress to complete) and there are failed uploads
    const wasInProgress = prevUploadsInProgressRef.current !== null && prevUploadsInProgressRef.current > 0;
    const allComplete = uploadsInProgress === 0 && wasInProgress;
    if (allComplete && totalFailed > 0 && activeTab === 'uploads') {
      setActiveTab('failed');
    }
    prevTotalFailedRef.current = totalFailed;
    prevUploadsInProgressRef.current = uploadsInProgress;
  }, [totalFailed, uploadsInProgress, activeTab]);

  const sortedUploads = useMemo(() => {
    const sorted = [...uploads].sort((a, b) => {
      // First, prioritize uploads still waiting for the final success callback.
      const aInProgress = a.status !== 'completed';
      const bInProgress = b.status !== 'completed';
      
      if (aInProgress && !bInProgress) return -1; // a comes first
      if (!aInProgress && bInProgress) return 1;  // b comes first
      
      // If both are in progress or both are completed/failed, sort by uploadIndex
      if (a.uploadIndex && b.uploadIndex) {
        return a.uploadIndex - b.uploadIndex; // Ascending order (1, 2, 3, 4)
      }
      
      // Fallback to timestamp comparison if uploadIndex not available
      if (a.timestamp && b.timestamp) {
        return b.timestamp - a.timestamp;
      }
      
      const aTime = Number.parseInt(a.id.split('-')[1]) || 0;
      const bTime = Number.parseInt(b.id.split('-')[1]) || 0;
      return bTime - aTime;
    });
    return sorted;
  }, [uploads]);

  // Keep failures for the same client together so, when several clients'
  // uploads are running at once, each client's problems read as one block.
  // Array sort is stable, so order within a client is unchanged.
  const sortedFailedUploads = useMemo(
    () => [...failedUploads].sort((a, b) =>
      (a.matterLabel || '').localeCompare(b.matterLabel || '')
    ),
    [failedUploads]
  );

  // Stable identity so memoised rows are not invalidated on every render.
  const handleCancelUpload = useCallback((uploadId) => {
    setUploadToCancel(uploadId);
    setShowCancelModal(true);
  }, []);

  const handleConfirmCancel = () => {
    if (uploadToCancel) {
      onRemoveUpload?.(uploadToCancel);
    }
    setShowCancelModal(false);
    setUploadToCancel(null);
  };

  const handleKeepUploading = () => {
    setShowCancelModal(false);
    setUploadToCancel(null);
  };

  const handleCloseNotification = () => {
    const uploadsInProgress = uploads.filter(upload => upload.status !== 'completed');
    if (uploadsInProgress.length > 0) {
      setUploadToCancel(null);
      setShowCancelModal(true);
    } else if (interruptedFailed.length > 0 && onResumeInterrupted) {
      // Closing the tray forgets which files were cut off. Make the user say
      // so, instead of losing the list to a stray click on the X.
      setIsCollapsed(false);
      setActiveTab('failed');
      setConfirmForget(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancelAll = () => {
    onCancelAll?.();
    setShowCancelModal(false);
    setUploadToCancel(null);
  };

  if (isCollapsed) {
    return (
      <CollapsedNotificationWrapper>
        <CollapsedHeaderControls>
          <CollapsedHeaderLeft>
            <CollapsedStatusContainer>
              {uploadStatus.status === 'upload_in_progress' && (
                <StatusRow>
                  <LoadingSpinner><div className="spinner"></div></LoadingSpinner>
                  <StatusText>{uploadStatus.message}</StatusText>
                </StatusRow>
              )}
              {uploadStatus.status === 'optimization_pending' && (
                <StatusRow>
                  <LoadingSpinner><div className="spinner"></div></LoadingSpinner>
                  <StatusText>{uploadStatus.message}</StatusText>
                </StatusRow>
              )}
              {uploadStatus.status === 'upload_complete' && completedUploads > 0 && (
                <StatusRow>
                  <SuccessStatusIcon><Check size={10} /></SuccessStatusIcon>
                  <StatusText success>{uploadStatus.message}</StatusText>
                </StatusRow>
              )}
              {!uploadsInProgress && totalFailed > 0 && (
                <StatusRow>
                  <WarningCircle size={19.5} color="#B31514" weight="fill" />
                  <StatusText error>Upload Failed. <span>{totalFailed}</span></StatusText>
                </StatusRow>
              )}
            </CollapsedStatusContainer>
          </CollapsedHeaderLeft>
          <CollapsedHeaderRight>
            <CollapsibleIcon>
              <RotatedIcon>
                <CaretDown size={16} onClick={() => setIsCollapsed(false)} data-testid="uploads-expand-btn"/>
              </RotatedIcon>
            </CollapsibleIcon>
            <CloseIcon>
              <X size={16} onClick={handleCloseNotification} />
            </CloseIcon>
          </CollapsedHeaderRight>
        </CollapsedHeaderControls>
      </CollapsedNotificationWrapper>
    );
  }

  return (
    <EnhancedNotificationWrapper>
      <HeaderControls>
        <TabContainer>
          <Tab active={activeTab === 'uploads'} onClick={() => setActiveTab('uploads')}>
            Uploads ({totalUploads})
          </Tab>
          {totalFailed > 0 && <Tab active={activeTab === 'failed'} onClick={() => setActiveTab('failed')}>
            Failed ({totalFailed})
          </Tab>}
        </TabContainer>
        <HeaderRight>
          <CollapsibleIcon>
            <CaretDown size={16} onClick={() => setIsCollapsed(true)} data-testid="uploads-collapse-btn"/>
          </CollapsibleIcon>
          <CloseIcon data-testid="upload-close-btn">
            <X size={16} onClick={handleCloseNotification} />
          </CloseIcon>
        </HeaderRight>
      </HeaderControls>

      <ContentArea>
        {activeTab === 'uploads' && (
          <>
            {uploads.length === 0 ? (
              <EmptyState>No uploads in progress</EmptyState>
            ) : (
              sortedUploads.map((upload, index) => (
                <UploadRow
                  key={upload.id || index}
                  upload={upload}
                  onCancel={handleCancelUpload}
                />
              ))
            )}
          </>
        )}

        {activeTab === 'failed' && (
          <>
            {failedUploads.length === 0 ? (
              <EmptyState>No failed uploads</EmptyState>
            ) : (
              <>
                {interruptedFailed.length > 0 && onResumeInterrupted && (
                  <ResumeBox data-testid="resume-interrupted-box">
                    <ResumeHeadline>
                      {interruptedFailed.length === 1
                        ? '1 file was cut off when the page reloaded'
                        : `${interruptedFailed.length} files were cut off when the page reloaded`}
                    </ResumeHeadline>
                    <ResumeText>
                      Files that finished are saved. Pick these files again to finish
                      them. You can pick the whole folder: only the cut-off files are
                      sent, each to its own client.
                    </ResumeText>
                    <ResumeButtons>
                      <RenameRetryButton
                        type="button"
                        onClick={() => resumeFilesInputRef.current?.click()}
                        disabled={isResuming}
                        data-testid="resume-pick-files-btn"
                      >
                        {isResuming ? 'Sending…' : 'Pick files'}
                      </RenameRetryButton>
                      <RenameRetryButton
                        type="button"
                        onClick={() => resumeFolderInputRef.current?.click()}
                        disabled={isResuming}
                        data-testid="resume-pick-folder-btn"
                      >
                        Pick a folder
                      </RenameRetryButton>
                    </ResumeButtons>
                    <input
                      ref={resumeFilesInputRef}
                      type="file"
                      multiple
                      hidden
                      onChange={handleResumePicked}
                      data-testid="resume-files-input"
                    />
                    <input
                      ref={resumeFolderInputRef}
                      type="file"
                      multiple
                      hidden
                      webkitdirectory=""
                      onChange={handleResumePicked}
                      data-testid="resume-folder-input"
                    />
                    {confirmForget && (
                      <ResumeText data-testid="resume-forget-confirm">
                        <strong>Forget these files?</strong> Closing this list means the
                        cut-off files will not be listed again.{' '}
                        <RenameRetryButton
                          type="button"
                          onClick={() => {
                            setConfirmForget(false);
                            onClose();
                          }}
                          data-testid="resume-forget-yes-btn"
                        >
                          Yes, forget them
                        </RenameRetryButton>{' '}
                        <RenameRetryButton
                          type="button"
                          onClick={() => setConfirmForget(false)}
                          data-testid="resume-forget-no-btn"
                        >
                          Keep the list
                        </RenameRetryButton>
                      </ResumeText>
                    )}
                    {resumeSummary && (
                      <ResumeText data-testid="resume-summary">
                        {resumeSummary.matched === 0
                          ? 'None of the files you picked were cut-off files. Nothing was sent.'
                          : `Sent ${resumeSummary.succeeded} of ${resumeSummary.matched} matching files.`}
                        {resumeSummary.failed > 0 && ` ${resumeSummary.failed} failed again; see below.`}
                        {resumeSummary.notInterrupted > 0 &&
                          ` ${resumeSummary.notInterrupted} picked files were skipped because they were not cut off.`}
                        {resumeSummary.stillWaiting > 0 &&
                          ` ${resumeSummary.stillWaiting} still waiting for their file.`}
                      </ResumeText>
                    )}
                  </ResumeBox>
                )}
                {retryableFailed.length > 0 && onRetryAllFailed && (
                  <RetryAllRow>
                    <RetryAllHint>
                      {retryableFailed.length === 1
                        ? '1 file can be sent again without re-selecting it.'
                        : `${retryableFailed.length} files can be sent again without re-selecting them.`}
                    </RetryAllHint>
                    <RenameRetryButton
                      type="button"
                      onClick={handleRetryAll}
                      disabled={isRetryingAll}
                      data-testid="retry-all-failed-btn"
                    >
                      {isRetryingAll ? 'Retrying…' : `Retry all (${retryableFailed.length})`}
                    </RenameRetryButton>
                  </RetryAllRow>
                )}
              {sortedFailedUploads.map((upload, index) => {
                const canRenameAndRetry = upload?.errorType === 'Duplicate File' && !!upload?.retryContext && !!onRenameAndRetry;
                const canRetry = !canRenameAndRetry && isRetryableFailedUpload(upload) && !!onRetryUpload;
                const isRetrying = !!retryingIds[upload.id] || isRetryingAll;
                const draftName = renameDrafts[upload.id] ?? upload.fileName;
                const isRenaming = !!renamingIds[upload.id];

                const handleRetryClick = async () => {
                  if (isRetrying) return;
                  setRetryingIds((prev) => ({ ...prev, [upload.id]: true }));
                  try {
                    await onRetryUpload(upload);
                  } finally {
                    setRetryingIds((prev) => {
                      const next = { ...prev };
                      delete next[upload.id];
                      return next;
                    });
                  }
                };

                const handleRenameSubmit = async () => {
                  if (!draftName.trim() || draftName.trim() === upload.fileName || isRenaming) {
                    return;
                  }
                  setRenamingIds((prev) => ({ ...prev, [upload.id]: true }));
                  try {
                    await onRenameAndRetry(upload, draftName.trim());
                  } finally {
                    setRenamingIds((prev) => {
                      const next = { ...prev };
                      delete next[upload.id];
                      return next;
                    });
                  }
                };

                return (
                  <FileItem key={upload.id || index}>
                    <FileIcon failed={true}>
                      {getFileIcon(upload.fileName, upload.type)}
                    </FileIcon>
                    <FileInfo>
                      {upload.matterLabel && (
                        <ClientLabel title={upload.matterLabel} data-testid={`failed-client-${upload.id}`}>
                          {upload.matterLabel}
                        </ClientLabel>
                      )}
                      <FileName title={upload.fileName}>{upload.fileName}</FileName>
                      <FileSize>{formatFileSize(upload.fileSize, upload.type)}</FileSize>
                      <ErrorMessage>{upload?.error || "Upload failed"}</ErrorMessage>
                      {canRenameAndRetry && (
                        <RenameRow>
                          <RenameInput
                            type="text"
                            value={draftName}
                            disabled={isRenaming}
                            onChange={(e) => setRenameDrafts((prev) => ({ ...prev, [upload.id]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenameSubmit();
                            }}
                            data-testid={`rename-input-${upload.id}`}
                            aria-label="New file name"
                          />
                          <RenameRetryButton
                            type="button"
                            onClick={handleRenameSubmit}
                            disabled={isRenaming || !draftName.trim() || draftName.trim() === upload.fileName}
                            data-testid={`rename-retry-btn-${upload.id}`}
                          >
                            {isRenaming ? 'Uploading…' : 'Upload as new name'}
                          </RenameRetryButton>
                        </RenameRow>
                      )}
                    </FileInfo>
                    {canRetry && (
                      <RetryButton
                        type="button"
                        onClick={handleRetryClick}
                        disabled={isRetrying}
                        data-testid={`retry-upload-btn-${upload.id}`}
                        aria-label={`Retry ${upload.fileName}`}
                      >
                        <ArrowClockwise size={14} weight="bold" />
                        {isRetrying ? 'Retrying…' : 'Retry'}
                      </RetryButton>
                    )}
                  </FileItem>
                );
              })}
              </>
            )}
          </>
        )}
      </ContentArea>

      <BottomStatus>
        {uploadStatus.status === 'upload_in_progress' && (
          <StatusRow>
            <LoadingSpinner><div className="spinner"></div></LoadingSpinner>
            <StatusText>{uploadStatus.message}</StatusText>
          </StatusRow>
        )}
        {uploadStatus.status === 'optimization_pending' && (
          <StatusRow>
            <LoadingSpinner><div className="spinner"></div></LoadingSpinner>
            <StatusText>{uploadStatus.message}</StatusText>
          </StatusRow>
        )}
        {uploadStatus.status === 'upload_complete' && (
          <StatusRow>
            <SuccessStatusIcon><Check size={10} /></SuccessStatusIcon>
            <StatusText success>{uploadStatus.message}</StatusText>
          </StatusRow>
        )}
        {totalFailed > 0 && (
          <StatusRow>
            <WarningCircle size={19.5} color="#B31514" weight="fill" />
            <StatusText error>Upload Failed. <span>{totalFailed}</span></StatusText>
          </StatusRow>
        )}
      </BottomStatus>

      <CancelUploadModal
        isOpen={showCancelModal}
        onClose={handleKeepUploading}
        onConfirm={uploadToCancel ? handleConfirmCancel : handleConfirmCancelAll}
        onKeepUploading={handleKeepUploading}
        fileName={uploadToCancel ? uploads.find(u => u.id === uploadToCancel)?.fileName : ''}
        isCancelAll={!uploadToCancel}
      />
    </EnhancedNotificationWrapper>
  );
};

EnhancedUploadNotification.propTypes = {
  uploads: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    fileName: PropTypes.string.isRequired,
    fileSize: PropTypes.number.isRequired,
    progress: PropTypes.number.isRequired,
    status: PropTypes.string,
    batchId: PropTypes.string, // ✅ REQUIRED
    matterLabel: PropTypes.string,
  })),
  failedUploads: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    fileName: PropTypes.string.isRequired,
    fileSize: PropTypes.number.isRequired,
    error: PropTypes.string,
    errorType: PropTypes.string,
    matterLabel: PropTypes.string,
    // The original File blob + upload params needed to retry in-place
    // (see uploadUtils.js's buildRetryContext). Absent when the file
    // itself is the problem.
    retryContext: PropTypes.object,
  })),
  onClose: PropTypes.func.isRequired,
  onRemoveUpload: PropTypes.func,
  onRetryUpload: PropTypes.func,
  onRetryAllFailed: PropTypes.func,
  onRenameAndRetry: PropTypes.func,
  onResumeInterrupted: PropTypes.func,
  onCancelAll: PropTypes.func,
};

export default EnhancedUploadNotification;
